import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash, SealCheck, Package, FilePdf, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { num, formatApiErrorDetail, API } from "../lib/api";
import { SectionHeader, ConfirmDialog } from "../components/shared";
import { itemLineDescription, ItemPicker } from "../components/LineItemText";
import { DataTable } from "../components/DataTable";
import { Button } from "../components/ui/button";
import { usePerms } from "../lib/perms";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = () => ({ project_id: "", date: today(), lines: [{ item_id: "", quantity: 1 }] });

export default function StockTransfers() {
  const qc = useQueryClient();
  const { can } = usePerms();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY());
  const [editId, setEditId] = useState(null);
  const [delRow, setDelRow] = useState(null);
  const [emailRow, setEmailRow] = useState(null);
  const [emailTo, setEmailTo] = useState("");

  const { data: transfers = [], isLoading } = useQuery({ queryKey: ["stock-transfers"], queryFn: async () => (await api.get("/stock-transfers")).data });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: items = [] } = useQuery({ queryKey: ["items"], queryFn: async () => (await api.get("/items")).data });

  const itemMap = Object.fromEntries(items.map((i) => [i.id, i]));
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["stock-transfers"] }); qc.invalidateQueries({ queryKey: ["items"] }); };

  const openNew = () => { setForm(EMPTY()); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ project_id: t.project_id, date: t.date, lines: (t.lines || []).map((l) => ({ item_id: l.item_id, quantity: l.quantity })) }); setEditId(t.id); setOpen(true); };

  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", quantity: 1 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const payload = () => ({ project_id: form.project_id, date: form.date, lines: form.lines.filter((l) => l.item_id).map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity || 0) })) });

  const saveMut = useMutation({
    mutationFn: async () => editId ? (await api.put(`/stock-transfers/${editId}`, payload())).data : (await api.post("/stock-transfers", payload())).data,
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editId ? "Transfer updated" : "Transfer request created"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"),
  });
  const postMut = useMutation({
    mutationFn: async (id) => (await api.post(`/stock-transfers/${id}/post`)).data,
    onSuccess: (r) => { invalidate(); toast.success(`${r.transfer_no} posted — stock moved to ${r.project_code || r.project_name}`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not post"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/stock-transfers/${id}`),
    onSuccess: () => { invalidate(); toast.success("Transfer deleted"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"),
  });
  const emailMut = useMutation({
    mutationFn: async () => (await api.post(`/stock-transfers/${emailRow.id}/send-email`, { recipients: [emailTo] })).data,
    onSuccess: (r) => { toast.success(`Picking document emailed to ${(r.sent_to || []).join(", ")}`); setEmailRow(null); setEmailTo(""); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email"),
  });

  const canSave = form.project_id && form.lines.some((l) => l.item_id && Number(l.quantity) > 0);

  return (
    <div className="animate-fade-up" data-testid="stock-transfers-page">
      <SectionHeader title="Stock Transfer Requests" subtitle="Move items from inventory out to a project">
        {can("projects.edit") && <Button onClick={openNew} data-testid="new-transfer-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9">
          <Plus size={16} weight="bold" className="mr-1" /> New Transfer Request
        </Button>}
      </SectionHeader>

      <DataTable
        testid="transfers-table"
        data={transfers}
        filterable={false}
        loading={isLoading}
        rowKey={(t) => t.id}
        rowTestid="transfer-row"
        empty="No stock transfer requests yet."
        columns={[
          { key: "transfer_no", header: "No.", cellClassName: "font-mono text-xs font-medium" },
          { key: "project", header: "Project", cell: (t) => <span className="text-sm font-medium">{t.project_code ? `${t.project_code} · ` : ""}{t.project_name}</span> },
          { key: "date", header: "Date", cellClassName: "text-sm text-muted-foreground" },
          { key: "lines", header: "Items", align: "right", cell: (t) => <span className="tabular text-sm">{(t.lines || []).length}</span> },
          { key: "status", header: "Status", align: "center", cell: (t) => (
            <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-semibold border ${t.posted ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid="transfer-status">{t.posted ? "Posted" : "Draft"}</span>
          ) },
          { key: "actions", header: "Actions", align: "right", sortable: false, cell: (t) => (
            <div className="flex items-center justify-end gap-1">
              <button onClick={() => window.open(`${API}/stock-transfers/${t.id}/pdf`, "_blank")} data-testid="print-transfer" title="Print picking document" className="p-1.5 text-muted-foreground hover:text-primary"><FilePdf size={16} weight="bold" /></button>
              {can("projects.edit") && <button onClick={() => { setEmailRow(t); setEmailTo(""); }} data-testid="email-transfer" title="Email picking document" className="p-1.5 text-muted-foreground hover:text-primary"><EnvelopeSimple size={16} weight="bold" /></button>}
              {!t.posted && can("projects.post") && <button onClick={() => postMut.mutate(t.id)} disabled={postMut.isPending} data-testid="post-transfer" title="Post — move stock to project" className="p-1.5 text-muted-foreground hover:text-emerald-600"><SealCheck size={16} weight="bold" /></button>}
              {!t.posted && can("projects.edit") && <button onClick={() => openEdit(t)} data-testid="edit-transfer" title="Edit" className="p-1.5 text-muted-foreground hover:text-primary"><Package size={16} /></button>}
              {!t.posted && can("projects.delete") && <button onClick={() => setDelRow(t)} data-testid="delete-transfer" title="Delete" className="p-1.5 text-muted-foreground hover:text-destructive"><Trash size={16} /></button>}
            </div>
          ) },
        ]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Package size={20} className="text-primary" /> {editId ? "Edit" : "New"} Stock Transfer Request</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline-label">Transfer to Project</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="transfer-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline-label">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="rounded-sm mt-1" data-testid="transfer-date" />
              </div>
            </div>

            <div className="border border-border rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="transfer-lines-table">
                <thead className="bg-secondary/40 border-b border-border">
                  <tr className="text-left">
                    <th className="py-2 px-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Item required</th>
                    <th className="py-2 px-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold text-right w-24">On Hand</th>
                    <th className="py-2 px-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold text-right w-28">Qty to Transfer</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((l, i) => {
                    const it = itemMap[l.item_id];
                    const short = it && Number(l.quantity) > Number(it.quantity || 0);
                    return (
                      <tr key={i} className="border-b border-border/60" data-testid="transfer-line-row">
                        <td className="py-1.5 px-2">
                          <ItemPicker value={l.item_id || ""} items={items} onChange={(v) => setLine(i, "item_id", v)} allowNone testid="transfer-line-item" />
                          {it && <span className="block mt-1 text-[11px] text-muted-foreground" data-testid="transfer-line-desc">{itemLineDescription(it)}</span>}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular text-xs text-muted-foreground">{it ? `${num(it.quantity)} ${it.unit_of_measure || ""}` : "—"}</td>
                        <td className="py-1.5 px-2 text-right">
                          <Input type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className={`rounded-sm h-8 num text-right ${short ? "ring-1 ring-rose-400 border-rose-400" : ""}`} data-testid="transfer-line-qty" />
                        </td>
                        <td className="py-1.5 px-1 text-right">{form.lines.length > 1 && <button onClick={() => removeLine(i)} className="p-1 text-muted-foreground hover:text-destructive" data-testid="transfer-line-delete"><Trash size={14} /></button>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button onClick={addLine} data-testid="transfer-add-line" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"><Plus size={14} weight="bold" /> Add item</button>
            <p className="text-xs text-muted-foreground">Posting the request reduces on-hand inventory and assigns the quantity to the project. A line in red exceeds available stock.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="transfer-cancel">Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!canSave || saveMut.isPending} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="transfer-save">{saveMut.isPending ? "Saving…" : editId ? "Save" : "Create Request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailRow} onOpenChange={(v) => { if (!v) { setEmailRow(null); setEmailTo(""); } }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="transfer-email-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><EnvelopeSimple size={20} className="text-primary" /> Email Picking Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Send the {emailRow?.transfer_no} picking document (PDF) to the site or project contact.</p>
            <div>
              <Label className="overline-label">Recipient email</Label>
              <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="site.contact@example.com" className="rounded-sm mt-1" data-testid="transfer-email-to" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEmailRow(null); setEmailTo(""); }} className="rounded-sm">Cancel</Button>
            <Button onClick={() => emailMut.mutate()} disabled={!emailTo || emailMut.isPending} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="transfer-email-send">{emailMut.isPending ? "Sending…" : "Send"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delRow}
        onOpenChange={(v) => { if (!v) setDelRow(null); }}
        title="Delete this transfer request?"
        description={delRow ? `${delRow.transfer_no} will be permanently deleted.` : ""}
        confirmLabel="Delete"
        testid="transfer-delete-confirm"
        onConfirm={() => { if (delRow) delMut.mutate(delRow.id); setDelRow(null); }}
      />
    </div>
  );
}
